"use client";

import { useState, useMemo, useEffect, useRef, useCallback, KeyboardEvent, createContext, useContext, Fragment } from "react";
import Link from "next/link";
import Image from "next/image";
import { publicProjects, type Project, type ProcessStep, type UserQuote } from "@/data/projects";
import { LiveDemoStage } from "@/components/case-study/LiveDemoStage";
import { useAdjacentProjects } from "@/hooks/useProjectData";
import { LightboxV2, type LightboxItem } from "@/components/ui/LightboxV2";
import { PlayIcon, ArrowRightIcon } from "@/components/ui/Button";
import { useMotionMode } from "@/context/MotionContext";
import { useThemeMode } from "@/context/ThemeContext";
import { MediaWithFallback } from "@/components/ui/MediaWithFallback";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Tooltip } from "@/components/ui/Tooltip";
import { ProjectCard } from "@/components/ui/ProjectCard";
import { playUIHover } from "@/lib/audio/spatialAudio";

// Context for project navigation within content
const ProjectNavContext = createContext<((slug: string) => void) | null>(null);

// A11y helper: handle Enter/Space for clickable elements
const handleKeyboardClick = (callback: () => void) => (e: KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    callback();
  }
};

// =============================================================================
// CASE STUDY INLINE
// =============================================================================
// Renders full case study content inline within spatial-os read mode
// Positioned below the OrbCard header strip

// =============================================================================
// UTILITY FUNCTIONS (simplified from CaseStudyPage)
// =============================================================================

// Parse ==orange== text (themed emphasis)
function parseOrange(text: string, keyPrefix: string = ''): React.ReactNode[] {
  const orangePattern = /==([^=]+)==/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = orangePattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...parseShimmer(text.slice(lastIndex, match.index), `${keyPrefix}pre-${match.index}-`));
    }
    parts.push(
      <span key={`${keyPrefix}orange-${match.index}`} className="font-semibold text-[var(--theme-base)]">
        {match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(...parseShimmer(text.slice(lastIndex), `${keyPrefix}post-${lastIndex}-`));
  }

  if (parts.length === 0) {
    return parseShimmer(text, keyPrefix);
  }

  return parts;
}

// Parse _shimmer_ text (gradient text effect)
function parseShimmer(text: string, keyPrefix: string = ''): React.ReactNode[] {
  const shimmerPattern = /_([^_]+)_/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = shimmerPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <span key={`${keyPrefix}shimmer-${match.index}`} className="font-semibold gradient-text">
        {match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

// Parse quote text - handles _shimmer_ and #crunchyroll mentions
function parseQuoteText(text: string, keyPrefix: string = ''): React.ReactNode[] {
  // First apply shimmer parsing for _text_ patterns
  const shimmerParsed = parseShimmer(text, keyPrefix);

  // Then handle #crunchyroll in any remaining string parts
  return shimmerParsed.flatMap((part, i) => {
    if (typeof part !== 'string') return part;

    const hashtagPattern = /(#crunchyroll)/gi;
    const subparts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = hashtagPattern.exec(part)) !== null) {
      if (match.index > lastIndex) {
        subparts.push(part.slice(lastIndex, match.index));
      }
      subparts.push(
        <span key={`${keyPrefix}hashtag-${i}-${match.index}`} className="font-semibold gradient-text">
          {match[1]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < part.length) {
      subparts.push(part.slice(lastIndex));
    }

    return subparts.length > 0 ? subparts : [part];
  });
}

// Parse **bold** text
function parseBold(text: string, keyPrefix: string = ''): React.ReactNode[] {
  const boldPattern = /\*\*([^*]+)\*\*/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = boldPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...parseOrange(text.slice(lastIndex, match.index), `${keyPrefix}pre-${match.index}-`));
    }
    parts.push(
      <strong key={`${keyPrefix}bold-${match.index}`} className="font-semibold text-[var(--foreground)]">
        {parseOrange(match[1], `${keyPrefix}bold-inner-${match.index}-`)}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(...parseOrange(text.slice(lastIndex), `${keyPrefix}post-${lastIndex}-`));
  }

  if (parts.length === 0) {
    return parseOrange(text, keyPrefix);
  }

  return parts;
}

// 4-point star icon with glow for project link tooltips (fallback)
function StarIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{
        filter: "drop-shadow(0 0 4px currentColor)",
        opacity: 0.9,
      }}
    >
      <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
    </svg>
  );
}

// External link icon (arrow pointing out)
function ExternalLinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ opacity: 0.8 }}
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// Internal project link with tooltip
function ProjectLink({ href, children }: { href: string; children: React.ReactNode }) {
  const onNavigate = useContext(ProjectNavContext);
  const slug = href.replace(/^\//, ''); // Remove leading slash

  // Check if this is a project link (not /ds, etc.)
  const projectSlugs = ['avp', 'esr', 'cmp', 'mp', 'rr', 'vpr', 'vux', 'rapid-reorder', 'panera-voice', 'panera-mobile', 'apple-vision-pro', 'end-slate', 'multiple-profiles', 'crunchyroll-profiles'];
  const isProjectLink = projectSlugs.some(s => slug === s || slug.startsWith(s + '/'));

  if (isProjectLink && onNavigate) {
    // URL slugs match project IDs directly (panera-mobile, panera-voice, avp, esr, cmp)
    const projectId = slug;

    // Look up the target project to get its orb icon
    const targetProject = publicProjects.find(p => p.id === projectId);
    const orbIcon = targetProject?.ctaIcon ? (
      <Image
        src={targetProject.ctaIcon}
        width={48}
        height={48}
        alt=""
        className="rounded-full"
      />
    ) : <StarIcon />;

    return (
      <Tooltip text="View case study" position="bottom" icon={orbIcon}>
        <button
          onClick={() => onNavigate(projectId)}
          onMouseEnter={() => playUIHover()}
          className="link-shimmer cursor-pointer"
        >
          {children}
        </button>
      </Tooltip>
    );
  }

  // Non-project internal link - use regular Link
  return (
    <Link href={href} className="link-shimmer cursor-pointer" onMouseEnter={() => playUIHover()}>
      {children}
    </Link>
  );
}

// Parse markdown links [text](url)
function parseMarkdownLinks(text: string): React.ReactNode[] {
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...parseBold(text.slice(lastIndex, match.index), `pre-${match.index}-`));
    }
    const url = match[2];
    const isInternal = url.startsWith('/');

    if (isInternal) {
      parts.push(
        <ProjectLink key={match.index} href={url}>
          {parseBold(match[1], `link-${match.index}-`)}
        </ProjectLink>
      );
    } else {
      parts.push(
        <Tooltip key={match.index} text="Open in new tab" position="bottom" icon={<ExternalLinkIcon />}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="link-external cursor-pointer"
            onMouseEnter={() => playUIHover()}
          >
            {parseBold(match[1], `link-${match.index}-`)}
          </a>
        </Tooltip>
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(...parseBold(text.slice(lastIndex), `post-${lastIndex}-`));
  }

  if (parts.length === 0) {
    return parseBold(text);
  }

  return parts;
}

// Highlight metrics in text
function highlightMetrics(text: string, keyPrefix: string = ''): React.ReactNode[] {
  const metricPattern = /(\$[\d.]+[KMB]\+?|[\d.,]+ hours|[\d.]+ seconds|[\d.]+%|[\d.]+★|[\d.]+[KMB]\+? users|[\d.]+[KMB]\+?|[\d.]+x|\d+\+? platforms|Day \d+|\d+(?:st|nd|rd|th)(?: QSR)?)/g;
  const parts = text.split(metricPattern);
  const testPattern = /^(\$[\d.]+[KMB]\+?|[\d.,]+ hours|[\d.]+ seconds|[\d.]+%|[\d.]+★|[\d.]+[KMB]\+? users|[\d.]+[KMB]\+?|[\d.]+x|\d+\+? platforms|Day \d+|I\/O \d{4}|\d+(?:st|nd|rd|th)(?: QSR)?|'[^']+')$/;

  return parts.map((part, i) => {
    if (testPattern.test(part)) {
      return (
        <span key={`${keyPrefix}metric-${i}`} className="font-semibold gradient-text">
          {part}
        </span>
      );
    }
    return part;
  });
}

// Parse content with markdown links and metric highlighting
function parseContent(text: string, keyPrefix: string = ''): React.ReactNode[] {
  const linkedParts = parseMarkdownLinks(text);
  return linkedParts.flatMap((part, i) =>
    typeof part === 'string' ? highlightMetrics(part, `${keyPrefix}part-${i}-`) : part
  );
}

// Split text into paragraphs by double newlines
function renderParagraphs(text: string, keyPrefix: string = ''): React.ReactNode[] {
  return text.split('\n\n').map((paragraph, i) => (
    <p key={`${keyPrefix}p-${i}`} className="mb-4 last:mb-0">
      {parseContent(paragraph, `${keyPrefix}p-${i}-`)}
    </p>
  ));
}

// =============================================================================
// VIEWPORT VIDEO COMPONENT
// =============================================================================
// Video component that plays only when in viewport (respects motion mode)

function ViewportVideo({
  src,
  poster,
  className,
  ariaLabel,
  onClick,
  onKeyDown,
  onHover,
  playbackRate = 1,
  fillParent = false,
}: {
  src: string;
  poster?: string;
  className?: string;
  ariaLabel?: string;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onHover?: () => void;
  playbackRate?: number;
  fillParent?: boolean; // Make wrapper fill parent with absolute inset-0
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { mode: motionMode } = useMotionMode();
  const [isPlaying, setIsPlaying] = useState(false);
  const [userInitiated, setUserInitiated] = useState(false);
  const [hasPlayedInline, setHasPlayedInline] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set playback rate
    video.playbackRate = playbackRate;

    // If motion is off and user hasn't initiated, don't autoplay
    if (motionMode === "off" && !userInitiated) {
      video.pause();
      setIsPlaying(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
          setIsPlaying(true);
        } else {
          video.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [playbackRate, motionMode, userInitiated]);

  // Reset state when motion mode changes
  useEffect(() => {
    if (motionMode === "on") {
      setUserInitiated(false);
      setHasPlayedInline(false);
    }
  }, [motionMode]);

  // Reset state when video source changes
  useEffect(() => {
    setUserInitiated(false);
    setHasPlayedInline(false);
    setIsPlaying(false);
  }, [src]);

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (!video) return;

    if (motionMode === "on") {
      if (onClick) onClick();
      return;
    }

    // Motion is OFF
    if (!hasPlayedInline) {
      // First click: play inline
      setUserInitiated(true);
      setHasPlayedInline(true);
      video.play().catch(() => {});
      setIsPlaying(true);
    } else if (onClick) {
      // Second click: open lightbox
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onKeyDown) {
      onKeyDown(e);
    } else if ((e.key === "Enter" || e.key === " ") && motionMode === "off" && !isPlaying) {
      e.preventDefault();
      handlePlayClick();
    }
  };

  const showPlayOverlay = motionMode === "off" && !isPlaying && !hasPlayedInline;
  const showExpandHint = !!onClick && (
    (motionMode === "off" && hasPlayedInline) ||
    (motionMode === "on" && isHovered)
  );

  // Extract rounded class from className to apply to wrapper
  const roundedClass = className?.match(/rounded-\w+/)?.[0] || "rounded-2xl";

  // Show fallback if video failed to load
  if (hasError) {
    return (
      <div
        className={`${fillParent ? 'absolute inset-0' : 'relative'} overflow-hidden ${roundedClass} flex items-center justify-center bg-[rgba(var(--foreground-rgb),0.05)] border border-dashed border-[rgba(var(--foreground-rgb),0.15)] ${className}`}
        style={fillParent ? undefined : { aspectRatio: "16/9" }}
        role="img"
        aria-label={`${ariaLabel || "Video"} (failed to load)`}
      >
        <div className="flex flex-col items-center gap-2 text-[rgba(var(--foreground-rgb),0.3)]">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="2" width="20" height="20" rx="2" />
            <path d="M10 8l6 4-6 4V8z" />
            <line x1="4" y1="4" x2="20" y2="20" />
          </svg>
          <span className="text-xs font-mono">Video unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${fillParent ? 'absolute inset-0' : 'relative'} overflow-hidden ${roundedClass}`}
      onMouseEnter={() => {
        setIsHovered(true);
        if (onClick) onHover?.();
      }}
      onMouseLeave={() => setIsHovered(false)}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        preload="metadata"
        loop
        muted
        playsInline
        aria-label={ariaLabel}
        className={className}
        role={onClick || showPlayOverlay ? "button" : undefined}
        tabIndex={onClick || showPlayOverlay ? 0 : undefined}
        onClick={handlePlayClick}
        onKeyDown={handleKeyDown}
        onError={() => setHasError(true)}
      />
      {showPlayOverlay && (
        <button
          className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer transition-opacity hover:bg-black/40"
          onClick={handlePlayClick}
          aria-label="Play video"
        >
          <PlayIcon size={64} className="text-white drop-shadow-lg" />
        </button>
      )}
      {showExpandHint && (
        <div className="absolute bottom-3 right-3 p-2 rounded-lg bg-black/50 backdrop-blur-sm
                        opacity-60 hover:opacity-100 transition-opacity pointer-events-none">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
          </svg>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SECTION COMPONENTS
// =============================================================================

function SectionHeader({ children, crtEnabled = true, size = 'default' }: { children: React.ReactNode; crtEnabled?: boolean; size?: 'default' | 'small' }) {
  const isSmall = size === 'small';
  return (
    <h2 className={`${isSmall ? "mb-4" : "mb-8"} text-center sm:text-left`}>
      <span
        className={`${isSmall ? 'text-[1rem] uppercase tracking-widest text-[rgba(var(--foreground-rgb),0.5)]' : 'text-[1.75rem] text-[var(--foreground)]'} font-bold inline-block`}
        style={{
          transform: crtEnabled && !isSmall ? 'perspective(400px) rotateX(10deg)' : 'none',
          transformOrigin: 'bottom center',
          textShadow: crtEnabled && !isSmall ? 'var(--crt-text-shadow)' : 'none',
        }}
      >
        {children}
      </span>
    </h2>
  );
}

function MetricPill({ value, label }: { value: string; label: string }) {
  return (
    <div className="glass no-hover relative flex flex-col items-center justify-center px-4 py-6 rounded-2xl text-center">
      <span className="text-xl font-bold gradient-text">{value}</span>
      <span className="text-xs text-[rgba(var(--foreground-rgb),0.5)] uppercase tracking-wide font-black">{label}</span>
    </div>
  );
}

function QuoteCard({ quote }: { quote: UserQuote }) {
  return (
    <div className="glass no-hover relative p-6 rounded-2xl">
      {quote.context && (
        <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mb-2">{highlightMetrics(quote.context, 'quote-context-')}</p>
      )}
      <p className="text-sm text-[var(--cs-body-text)] leading-relaxed">
        “{parseQuoteText(quote.text, 'quote-text-')}”
      </p>
      {quote.author && (
        <p className="text-xs text-[rgba(var(--foreground-rgb),0.5)] mt-2">— {quote.author}</p>
      )}
    </div>
  );
}

// Helper to render a single media item (image or video)
function ProcessMedia({
  media,
  lightboxId,
  openLightbox,
  className = "",
  forceAspectRatio,
  onHover,
}: {
  media: import("@/data/projects").ProcessStepMedia;
  lightboxId: string;
  openLightbox: (srcOrId: string, useId?: boolean) => void;
  className?: string;
  forceAspectRatio?: boolean; // Force 5:2 aspect ratio for consistent grid heights
  onHover?: () => void;
}) {
  // Support both src-based media and YouTube embeds
  if (!media.src && !media.youtubeId) return null;

  // Wrapper for forced aspect ratio
  const aspectWrapper = forceAspectRatio ? "relative w-full overflow-hidden rounded-2xl" : "";
  const aspectStyle = forceAspectRatio ? { aspectRatio: "16/9" } : {}; // Match typical screenshot aspect ratio
  const innerClass = forceAspectRatio ? "absolute inset-0 w-full h-full object-cover" : "w-full";

  return (
    <div className={className}>
      {media.type === 'youtube' && media.youtubeId ? (
        <div className="relative w-full aspect-video rounded-2xl overflow-hidden">
          <iframe
            src={`https://www.youtube.com/embed/${media.youtubeId}?rel=0&modestbranding=1`}
            title={media.caption || "YouTube video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      ) : media.type === 'video' ? (
        // Videos with aspectRatio get wrapped in a container with object-cover
        media.aspectRatio ? (
          <div
            className="relative w-full overflow-hidden rounded-2xl"
            style={{ aspectRatio: media.aspectRatio }}
          >
            <ViewportVideo
              src={media.src!}
              className="w-full h-full object-cover cursor-zoom-in"
              ariaLabel={media.caption || "Process step media"}
              playbackRate={media.playbackRate}
              onClick={() => openLightbox(lightboxId, true)}
              onKeyDown={handleKeyboardClick(() => openLightbox(lightboxId, true))}
              onHover={onHover}
              fillParent
            />
          </div>
        ) : (
          <ViewportVideo
            src={media.src!}
            className="w-full rounded-2xl cursor-zoom-in"
            ariaLabel={media.caption || "Process step media"}
            playbackRate={media.playbackRate}
            onClick={() => openLightbox(lightboxId, true)}
            onKeyDown={handleKeyboardClick(() => openLightbox(lightboxId, true))}
            onHover={onHover}
          />
        )
      ) : (
        <div className={aspectWrapper} style={aspectStyle}>
          <MediaWithFallback
            src={media.src!}
            alt={media.caption || "Process step"}
            className={`${innerClass} rounded-2xl cursor-zoom-in ${forceAspectRatio ? 'object-cover' : ''}`}
            onClick={() => openLightbox(lightboxId, true)}
            onMouseEnter={onHover}
            tabIndex={0}
            onKeyDown={handleKeyboardClick(() => openLightbox(lightboxId, true))}
          />
        </div>
      )}
    </div>
  );
}

function ProcessStepCard({
  step,
  index,
  openLightbox,
  onHover,
}: {
  step: ProcessStep;
  index: number;
  openLightbox: (srcOrId: string, useId?: boolean) => void;
  onHover?: () => void;
}) {
  // Determine grid columns based on image count
  const getGridCols = (count: number) => {
    if (count === 4) return "grid-cols-2"; // 2x2 grid
    if (count === 3) return "grid-cols-1 sm:grid-cols-3";
    if (count === 2) return "grid-cols-1 sm:grid-cols-2";
    return "grid-cols-1";
  };

  const isSideBySide = step.layout === "side-by-side-left" || step.layout === "side-by-side";

  // Side-by-side layout: image left, all text right
  if (isSideBySide && step.image && step.image.src) {
    return (
      <div className="mb-10 last:mb-0">
        <h3 className="flex items-center gap-3 mb-4">
          <span
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
            style={{
              backgroundColor: 'rgba(var(--theme-base-rgb), 0.15)',
              color: 'var(--theme-base)',
            }}
          >
            {index + 1}
          </span>
          <span className="text-[1.125rem] font-bold text-balance text-[var(--theme-base)]">
            {step.title}
          </span>
        </h3>
        <div className="flex flex-col sm:flex-row gap-6">
          {/* Image on left */}
          <div className="sm:w-[45%] flex-shrink-0">
            <ProcessMedia
              media={step.image}
              lightboxId={`process-${index}-main`}
              openLightbox={openLightbox}
              onHover={onHover}
            />
            {step.image.caption && (
              <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">{step.image.caption}</p>
            )}
          </div>
          {/* Text on right */}
          <div className="flex-1 text-base text-[var(--cs-body-text)] leading-relaxed">
            {renderParagraphs(step.content, `process-${index}-`)}
            {step.contentAfter && (
              <div className="mt-4">
                {renderParagraphs(step.contentAfter, `process-${index}-after-`)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10 last:mb-0">
      <h3 className="flex items-center gap-3 mb-4">
        <span
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
          style={{
            backgroundColor: 'rgba(var(--theme-base-rgb), 0.15)',
            color: 'var(--theme-base)',
          }}
        >
          {index + 1}
        </span>
        <span className="text-[1.125rem] font-bold text-balance text-[var(--theme-base)]">
          {step.title}
        </span>
      </h3>
      <div className="text-base text-[var(--cs-body-text)] leading-relaxed">
        {renderParagraphs(step.content, `process-${index}-`)}
      </div>

      {/* Stats - lighter style without glass */}
      {step.stats && step.stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-6">
          {step.stats.map((stat, i) => (
            <div key={i} className="flex flex-col items-center py-4">
              <span className="text-2xl font-black gradient-text">
                {stat.number}{stat.suffix || '%'}
              </span>
              <span className="text-xs text-[rgba(var(--foreground-rgb),0.5)] uppercase tracking-wide font-bold text-center">
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Single image or YouTube embed */}
      {step.image && (step.image.src || step.image.youtubeId) && (
        <div className="mt-6">
          <ProcessMedia
            media={step.image}
            lightboxId={`process-${index}-main`}
            openLightbox={openLightbox}
            onHover={onHover}
          />
          {step.image.caption && (
            <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">{step.image.caption}</p>
          )}
        </div>
      )}

      {/* Images before contentAfter (explicit via imagesBeforeContentAfter) */}
      {step.imagesBeforeContentAfter && step.imagesBeforeContentAfter.length > 0 && (() => {
        const hasDescriptions = step.imagesBeforeContentAfter.some(img => img.description);

        // If any have descriptions, use stacked layout with side-by-side for those with descriptions
        if (hasDescriptions) {
          return (
            <div className="space-y-8 mt-6">
              {step.imagesBeforeContentAfter.map((img, imgIdx) => (
                img.description ? (
                  // Side-by-side layout: description left, image right
                  <div key={imgIdx} className="flex flex-col sm:flex-row gap-6">
                    <div className="sm:w-[45%] flex-shrink-0 text-base text-[var(--cs-body-text)] leading-relaxed">
                      {parseContent(img.description, `process-${index}-before-desc-${imgIdx}-`)}
                    </div>
                    <div className="flex-1">
                      <ProcessMedia
                        media={img}
                        lightboxId={`process-${index}-before-${imgIdx}`}
                        openLightbox={openLightbox}
                        onHover={onHover}
                      />
                      {img.caption && (
                        <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">{img.caption}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  // Standard single-column layout
                  <div key={imgIdx}>
                    <ProcessMedia
                      media={img}
                      lightboxId={`process-${index}-before-${imgIdx}`}
                      openLightbox={openLightbox}
                      onHover={onHover}
                    />
                    {img.caption && (
                      <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">{img.caption}</p>
                    )}
                  </div>
                )
              ))}
            </div>
          );
        }

        // No descriptions - use grid layout
        return (
          <div className={`grid ${step.imagesBeforeLayout === 'single' ? 'grid-cols-1' : getGridCols(step.imagesBeforeContentAfter.length)} gap-4 mt-6`}>
            {step.imagesBeforeContentAfter.map((img, imgIdx) => (
              <div key={imgIdx}>
                <ProcessMedia
                  media={img}
                  lightboxId={`process-${index}-before-${imgIdx}`}
                  openLightbox={openLightbox}
                  forceAspectRatio={step.imagesBeforeLayout !== 'single' && step.imagesBeforeContentAfter!.length === 2}
                  onHover={onHover}
                />
                {img.caption && (
                  <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">{img.caption}</p>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Images before contentAfter (implicit - only if no explicit imagesBeforeContentAfter) */}
      {step.images && step.images.length > 0 && !step.imagesBeforeContentAfter && (
        <div className={`grid ${step.imageLayout === 'single' ? 'grid-cols-1' : getGridCols(step.images.length)} gap-4 mt-6`}>
          {step.images.map((img, imgIdx) => (
            <div key={imgIdx}>
              <ProcessMedia
                media={img}
                lightboxId={`process-${index}-${imgIdx}`}
                openLightbox={openLightbox}
                forceAspectRatio={step.imageLayout !== 'single' && step.images!.length === 2}
                onHover={onHover}
              />
              {img.caption && (
                <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">{img.caption}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Content after images */}
      {step.contentAfter && (
        <div className="text-base text-[var(--cs-body-text)] leading-relaxed mt-6">
          {renderParagraphs(step.contentAfter, `process-${index}-after-`)}
        </div>
      )}

      {/* Images after contentAfter (only when imagesBeforeContentAfter is used) */}
      {step.images && step.images.length > 0 && step.imagesBeforeContentAfter && (
        <div className={`grid ${step.imageLayout === 'single' ? 'grid-cols-1' : getGridCols(step.images.length)} gap-4 mt-6`}>
          {step.images.map((img, imgIdx) => (
            <div key={imgIdx}>
              <ProcessMedia
                media={img}
                lightboxId={`process-${index}-${imgIdx}`}
                openLightbox={openLightbox}
                forceAspectRatio={step.imageLayout !== 'single' && step.images!.length === 2}
                onHover={onHover}
              />
              {img.caption && (
                <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">{img.caption}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Image groups - multiple grids with optional labels (renders after contentAfter) */}
      {step.imageGroups && step.imageGroups.length > 0 && (
        <div className="space-y-6 mt-6">
          {step.imageGroups.map((group, groupIdx) => (
            <div key={groupIdx}>
              {group.label && (
                <p className="text-xs text-[rgba(var(--foreground-rgb),0.5)] uppercase tracking-wide font-bold mb-3">{group.label}</p>
              )}
              <div className={`grid ${getGridCols(group.images.length)} gap-4`}>
                {group.images.map((img, imgIdx) => (
                  <div key={imgIdx}>
                    <ProcessMedia
                      media={img}
                      lightboxId={`process-${index}-group-${groupIdx}-${imgIdx}`}
                      openLightbox={openLightbox}
                      forceAspectRatio={group.images.length === 2}
                      onHover={onHover}
                    />
                    {img.caption && (
                      <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">{img.caption}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Conclusion - final wrap-up text after all images */}
      {step.conclusion && (
        <div className="text-base text-[var(--cs-body-text)] leading-relaxed mt-6">
          {renderParagraphs(step.conclusion, `process-${index}-conclusion-`)}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface CaseStudyInlineProps {
  project: Project;
  scrollProgress?: number; // 0-1 morph progress
  onClose: () => void;
  onNavigateToProject?: (slug: string) => void;
  onLightboxChange?: (isOpen: boolean) => void; // Callback when lightbox opens/closes
  orbIndex?: number; // Project's orb index for audio pitch (1-6 for projects, 0 for profile)
  crtEnabled?: boolean;
}

export function CaseStudyInline({
  project,
  scrollProgress = 1,
  onClose,
  onNavigateToProject,
  onLightboxChange,
  orbIndex,
  crtEnabled = true,
}: CaseStudyInlineProps) {
  // Theme colors come from global CSS vars (set by page.tsx theme animation)
  // No local brandColor needed - use var(--theme-base) etc.
  const { mode: themeMode } = useThemeMode();
  const isLightMode = themeMode === "light";
  const bodyTextOpacity = isLightMode ? 1.0 : 0.85;

  // Get adjacent projects for next/prev navigation
  const { prevProject, nextProject } = useAdjacentProjects(project.id);

  // Project-specific hover sound (uses orb's pentatonic pitch)
  const handleProjectHover = useCallback(() => playUIHover(orbIndex), [orbIndex]);

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Floating nav state
  const isMobile = useIsMobile();
  const [navHovered, setNavHovered] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('overview');
  const mobileNavTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Build sections array based on project content
  const sections = useMemo(() => {
    const sectionList = [{ id: 'overview', label: 'Overview' }];
    if (project.challenge) sectionList.push({ id: 'challenge', label: 'Challenge' });
    if (project.solution) sectionList.push({ id: 'solution', label: 'Solution' });
    if (project.process && project.process.length > 0) sectionList.push({ id: 'process', label: 'Process' });
    if (project.results && project.results.length > 0) sectionList.push({ id: 'results', label: 'Results' });
    if (project.learnings && project.learnings.length > 0) sectionList.push({ id: 'takeaways', label: 'Takeaways' });
    return sectionList;
  }, [project]);

  // Scroll to section
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Auto-hide mobile nav after 2.5s
  useEffect(() => {
    if (mobileNavOpen && isMobile) {
      if (mobileNavTimeoutRef.current) clearTimeout(mobileNavTimeoutRef.current);
      mobileNavTimeoutRef.current = setTimeout(() => setMobileNavOpen(false), 2500);
    }
    return () => {
      if (mobileNavTimeoutRef.current) clearTimeout(mobileNavTimeoutRef.current);
    };
  }, [mobileNavOpen, isMobile]);

  // Detect active section using IntersectionObserver (works with any scroll container)
  useEffect(() => {
    const sectionIds = sections.map(s => s.id);
    const visibleSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleSections.add(entry.target.id);
          } else {
            visibleSections.delete(entry.target.id);
          }
        });

        // Find the first visible section in order
        for (const id of sectionIds) {
          if (visibleSections.has(id)) {
            setActiveSection(id);
            break;
          }
        }
      },
      {
        rootMargin: '-20% 0px -70% 0px', // Trigger when section is in top 30% of viewport
        threshold: 0,
      }
    );

    // Observe all section elements
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  // Notify parent when lightbox opens/closes
  useEffect(() => {
    onLightboxChange?.(lightboxIndex !== null);
  }, [lightboxIndex, onLightboxChange]);

  // Build array of all lightbox items for navigation (ordered to match visual flow)
  const lightboxItems = useMemo((): LightboxItem[] => {
    const items: LightboxItem[] = [];

    // 1. Hero video or image
    if (project.heroVideo) {
      items.push({ src: project.heroVideo, alt: project.title, caption: project.heroCaption, type: "video" });
    } else if (project.heroImage) {
      items.push({ src: project.heroImage, alt: project.title, caption: project.heroCaption });
    }

    // 2. Challenge image(s)
    if (project.challengeImages && project.challengeImages.length > 0) {
      project.challengeImages.forEach((img) => {
        items.push({ src: img.src, alt: "Challenge", caption: img.caption });
      });
    } else if (project.challengeImage) {
      items.push({ src: project.challengeImage, alt: "Challenge", caption: project.challengeImageCaption });
    }

    // 3. Solution media
    if (project.solutionMedia) {
      items.push({
        src: project.solutionMedia.src,
        alt: "Solution",
        caption: project.solutionMedia.caption,
        type: project.solutionMedia.type === "video" ? "video" : "image",
      });
    }

    // 4. Process step images/videos/youtube
    if (project.process) {
      project.process.forEach((step, stepIdx) => {
        // Single image or YouTube
        if (step.image && step.image.src) {
          items.push({
            id: `process-${stepIdx}-main`,
            src: step.image.src,
            alt: step.title,
            caption: step.image.caption,
            type: step.image.type === "video" ? "video" : "image",
          });
        } else if (step.image && step.image.youtubeId) {
          items.push({
            id: `process-${stepIdx}-main`,
            youtubeId: step.image.youtubeId,
            alt: step.title,
            caption: step.image.caption,
            type: "youtube",
          });
        }
        // Images before contentAfter
        if (step.imagesBeforeContentAfter) {
          step.imagesBeforeContentAfter.forEach((img, imgIdx) => {
            if (img.src) {
              items.push({
                id: `process-${stepIdx}-before-${imgIdx}`,
                src: img.src,
                alt: step.title,
                caption: img.caption,
                type: img.type === "video" ? "video" : "image",
              });
            }
          });
        }
        // Multiple images
        if (step.images) {
          step.images.forEach((img, imgIdx) => {
            if (img.src) {
              items.push({
                id: `process-${stepIdx}-${imgIdx}`,
                src: img.src,
                alt: step.title,
                caption: img.caption,
                type: img.type === "video" ? "video" : "image",
              });
            }
          });
        }
        // Image groups
        if (step.imageGroups) {
          step.imageGroups.forEach((group, groupIdx) => {
            group.images.forEach((img, imgIdx) => {
              if (img.src) {
                items.push({
                  id: `process-${stepIdx}-group-${groupIdx}-${imgIdx}`,
                  src: img.src,
                  alt: step.title,
                  caption: img.caption,
                  type: img.type === "video" ? "video" : "image",
                });
              }
            });
          });
        }
      });
    }

    // 5. Conclusion images
    if (project.conclusionImages) {
      project.conclusionImages.forEach((img) => {
        items.push({ src: img.src, alt: "Results", caption: img.caption });
      });
    }

    // 6. Recognition images
    if (project.recognitionImages) {
      project.recognitionImages.forEach((img) => {
        items.push({ src: img.src, alt: "Recognition", caption: img.caption });
      });
    }

    return items;
  }, [project]);

  // Helper to open lightbox at a specific image (by id or src)
  const openLightbox = (srcOrId: string, useId = false) => {
    const index = useId
      ? lightboxItems.findIndex((item) => item.id === srcOrId)
      : lightboxItems.findIndex((item) => item.src === srcOrId);
    if (index !== -1) {
      setLightboxIndex(index);
    }
  };

  return (
    <ProjectNavContext.Provider value={onNavigateToProject || null}>
    <>
    {/* Content flows naturally - no fixed positioning, no scroll container */}
    {/* Parent in page.tsx handles scrolling */}
    <div
      className="max-w-[900px] mx-auto px-3 sm:px-6 pb-32"
      style={{ '--cs-body-text': `rgba(var(--foreground-rgb), ${bodyTextOpacity})` } as React.CSSProperties}
    >
          {/* Project Header */}
          <header id="overview" className="text-center mb-10">
            {/* Company + Timeline tag */}
            <p
              className="text-xs uppercase tracking-widest mb-3 text-[var(--theme-base)] font-black"
            >
              {project.company}{project.timeline && ` • ${project.timeline}`}
            </p>
            {/* Title - trapezoid perspective for depth */}
            <h1
              className="font-bold gradient-text mb-3 text-balance leading-tight break-words"
              style={{
                fontSize: 'clamp(1rem, 6vw, 3.5rem)',
                transform: crtEnabled ? 'perspective(400px) rotateX(10deg)' : 'none',
                transformOrigin: 'bottom center',
                textShadow: crtEnabled ? 'var(--crt-text-shadow)' : 'none',
                wordBreak: 'break-word',
              }}
            >
              {project.title}
            </h1>
            {/* Subtitle if available */}
            {project.subtitle && (
              <p className="text-sm text-[rgba(var(--foreground-rgb),0.6)]">
                {project.subtitle}
              </p>
            )}
          </header>

          {/* Key Impact - glass container with inline header */}
          {project.keyImpact && (
            <section className="mb-10">
              <div className="glass no-hover p-8 rounded-2xl">
                <p className="text-[rgba(var(--foreground-rgb),0.5)] text-sm uppercase tracking-[0.15em] font-black mb-4">
                  Key Impact
                </p>
                <div className="text-[var(--foreground)] text-base leading-relaxed">
                  {renderParagraphs(project.keyImpact, 'key-impact-')}
                </div>
              </div>
            </section>
          )}

          {/* Squad - text-based grid layout */}
          {project.squad && project.squad.members.length > 0 && (() => {
            const leads = project.squad.members.filter(m => m.isLead);
            const contributors = project.squad.members.filter(m => !m.isLead);
            const hasLeadsAndContributors = leads.length > 0 && contributors.length > 0;

            const renderMember = (member: typeof project.squad.members[0], i: number) => (
              <div key={i} className="text-center">
                <p
                  className={`text-xs uppercase tracking-[0.15em] font-black mb-1 ${member.isMe ? 'text-[var(--theme-base)]' : ''}`}
                  style={{ color: member.isMe ? undefined : member.isLead ? 'rgba(var(--foreground-rgb), 0.9)' : 'rgba(var(--foreground-rgb), 0.5)' }}
                >
                  {member.role}
                </p>
                <p
                  className={`text-base font-semibold whitespace-nowrap ${member.isMe ? 'gradient-text' : ''}`}
                  style={{ color: member.isMe ? undefined : member.isLead ? 'rgba(var(--foreground-rgb), 0.9)' : 'rgba(var(--foreground-rgb), 0.7)' }}
                >
                  {member.name || '—'}
                </p>
              </div>
            );

            return (
              <section id="squad" className="mb-10">
                {hasLeadsAndContributors ? (
                  <>
                    {/* Mobile: simple 2-col grid for all */}
                    <div className="grid gap-4 grid-cols-2 sm:hidden">
                      {project.squad.members.map(renderMember)}
                    </div>
                    {/* Desktop: all members in single 3-col grid */}
                    <div className="hidden sm:grid gap-x-6 gap-y-8 grid-cols-3">
                      {leads.map(renderMember)}
                      {contributors.map(renderMember)}
                    </div>
                  </>
                ) : (
                  <div className={`grid gap-4 sm:gap-6 ${
                    project.squad.members.length <= 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'
                  }`}>
                    {project.squad.members.map(renderMember)}
                  </div>
                )}
              </section>
            );
          })()}

          {/* Hero Section */}
          <section className="mb-12">
            {/* Hero media */}
            {(project.heroVideo || project.heroImage) && (
              <div className="mb-6">
                {project.heroVideo ? (
                  <ViewportVideo
                    src={project.heroVideo}
                    className="w-full rounded-2xl cursor-zoom-in"
                    ariaLabel={project.title}
                    onClick={() => openLightbox(project.heroVideo!)}
                    onKeyDown={handleKeyboardClick(() => openLightbox(project.heroVideo!))}
                    onHover={handleProjectHover}
                  />
                ) : project.heroImage ? (
                  <MediaWithFallback
                    src={project.heroImage}
                    alt={project.title}
                    className="w-full rounded-2xl cursor-zoom-in"
                    onClick={() => openLightbox(project.heroImage!)}
                    onMouseEnter={handleProjectHover}
                    tabIndex={0}
                    onKeyDown={handleKeyboardClick(() => openLightbox(project.heroImage!))}
                  />
                ) : null}
              </div>
            )}
            {project.heroCaption && (
              <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] text-center mb-6">
                {parseContent(project.heroCaption, 'hero-caption-')}
              </p>
            )}

            {/* Hero description */}
            {project.heroDescription && (
              <div className="text-sm text-[var(--cs-body-text)] leading-relaxed font-black text-center">
                {renderParagraphs(project.heroDescription, 'hero-desc-')}
              </div>
            )}

            {/* Live prototype stages — hero position. The working prototype is
                the hero artifact (prototype-primary case studies, e.g. IDS). */}
            {project.liveDemos
              ?.filter((d) => (d.position ?? "hero") === "hero")
              .map((demo) => (
                <div className="mt-8" key={demo.src}>
                  <LiveDemoStage demo={demo} themeColor={project.brandColor ?? "#FF5E00"} />
                </div>
              ))}
          </section>

          {/* Divider */}
          <hr className="border-t border-[rgba(var(--foreground-rgb),0.1)] mb-12" />

          {/* Challenge */}
          {project.challenge && (
            <section id="challenge" className="mb-12">
              <SectionHeader crtEnabled={crtEnabled}>Challenge</SectionHeader>
              {(() => {
                const paragraphs = project.challenge.split('\n\n');
                const imageAfter = project.challengeImageAfterParagraph ?? 0;
                return paragraphs.map((paragraph, i) => (
                  <Fragment key={i}>
                    <p className="text-base text-[var(--cs-body-text)] leading-relaxed mb-4">
                      {parseContent(paragraph, `challenge-${i}-`)}
                    </p>
                    {/* Challenge images (plural) after specified paragraph */}
                    {i === imageAfter && project.challengeImages && project.challengeImages.length > 0 && (
                      <div className={`grid ${project.challengeImages.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'} gap-4 my-6`}>
                        {project.challengeImages.map((img, idx) => (
                          <div key={idx}>
                            <MediaWithFallback
                              src={img.src}
                              alt={img.caption || "Challenge context"}
                              className="w-full rounded-2xl cursor-zoom-in"
                              onClick={() => openLightbox(img.src)}
                              onMouseEnter={handleProjectHover}
                              tabIndex={0}
                              onKeyDown={handleKeyboardClick(() => openLightbox(img.src))}
                            />
                            {img.caption && (
                              <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">
                                {img.caption}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Single challenge image after specified paragraph (fallback) */}
                    {i === imageAfter && project.challengeImage && !project.challengeImages && (
                      <div className="my-6">
                        <MediaWithFallback
                          src={project.challengeImage}
                          alt="Challenge visualization"
                          className="w-full rounded-2xl cursor-zoom-in"
                          onClick={() => openLightbox(project.challengeImage!)}
                          onMouseEnter={handleProjectHover}
                          tabIndex={0}
                          onKeyDown={handleKeyboardClick(() => openLightbox(project.challengeImage!))}
                        />
                        {project.challengeImageCaption && (
                          <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">
                            {project.challengeImageCaption}
                          </p>
                        )}
                      </div>
                    )}
                  </Fragment>
                ));
              })()}
            </section>
          )}

          {/* Solution */}
          {project.solution && (
            <section id="solution" className="mb-12">
              <SectionHeader crtEnabled={crtEnabled}>Solution</SectionHeader>
              {project.solutionMedia && (
                <div className="mb-6">
                  {project.solutionMedia.type === 'video' ? (
                    <ViewportVideo
                      src={project.solutionMedia.src}
                      className="w-full rounded-2xl cursor-zoom-in"
                      ariaLabel="Solution visualization"
                      onClick={() => openLightbox(project.solutionMedia!.src)}
                      onKeyDown={handleKeyboardClick(() => openLightbox(project.solutionMedia!.src))}
                      onHover={handleProjectHover}
                    />
                  ) : (
                    <MediaWithFallback
                      src={project.solutionMedia.src}
                      alt="Solution visualization"
                      className="w-full rounded-2xl cursor-zoom-in"
                      onClick={() => openLightbox(project.solutionMedia!.src)}
                      onMouseEnter={handleProjectHover}
                      tabIndex={0}
                      onKeyDown={handleKeyboardClick(() => openLightbox(project.solutionMedia!.src))}
                    />
                  )}
                  {project.solutionMedia.caption && (
                    <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">
                      {project.solutionMedia.caption}
                    </p>
                  )}
                </div>
              )}
              <div className="text-base text-[var(--cs-body-text)] leading-relaxed">
                {renderParagraphs(project.solution, 'solution-')}
              </div>
            </section>
          )}

          {/* Process */}
          {project.process && project.process.length > 0 && (
            <section id="process" className="mb-12">
              <SectionHeader crtEnabled={crtEnabled}>Process</SectionHeader>
              {project.process.map((step, i) => (
                <ProcessStepCard key={i} step={step} index={i} openLightbox={openLightbox} onHover={handleProjectHover} />
              ))}
            </section>
          )}

          {/* Live prototype stages — second act. After the process arc, the
              narrative hands the reader the working thing again (e.g. IDS n5). */}
          {project.liveDemos?.some((d) => d.position === "secondAct") && (
            <section className="mb-12">
              {project.liveDemos
                .filter((d) => d.position === "secondAct")
                .map((demo) => (
                  <div className="mt-8 first:mt-0" key={demo.src}>
                    <LiveDemoStage demo={demo} themeColor={project.brandColor ?? "#FF5E00"} />
                  </div>
                ))}
            </section>
          )}

          {/* Divider before Results */}
          {project.results && project.results.length > 0 && (
            <hr className="border-t border-[rgba(var(--foreground-rgb),0.1)] mb-12" />
          )}

          {/* Results */}
          {project.results && project.results.length > 0 && (
            <section id="results" className="mb-12">
              <SectionHeader crtEnabled={crtEnabled}>Results</SectionHeader>
              <div className={`grid gap-3 mb-6 ${
                project.results.length === 4
                  ? 'grid-cols-2'
                  : 'grid-cols-2 sm:grid-cols-3'
              }`}>
                {project.results.map((result, i) => (
                  <MetricPill key={i} value={result.value} label={result.label} />
                ))}
              </div>
              {project.resultsCaption && (
                <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] text-center">
                  {project.resultsCaption}
                </p>
              )}

              {/* Conclusion Images within Results */}
              {project.conclusionImages && project.conclusionImages.length > 0 && (
                <div className={`grid ${project.conclusionImages.length === 2 && project.conclusionImageLayout !== 'single' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-4 mt-8`}>
                  {project.conclusionImages.map((img, i) => (
                    <div key={i}>
                      <MediaWithFallback
                        src={img.src}
                        alt={img.caption || "Results"}
                        className="w-full rounded-2xl cursor-zoom-in"
                        onClick={() => openLightbox(img.src)}
                        onMouseEnter={handleProjectHover}
                        tabIndex={0}
                        onKeyDown={handleKeyboardClick(() => openLightbox(img.src))}
                      />
                      {img.caption && (
                        <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">
                          {img.caption}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Ratings Comparison */}
              {project.ratingsComparison && project.ratingsComparison.length > 0 && (
                <div className="mt-8">
                  <p className="text-[rgba(var(--foreground-rgb),0.5)] text-xs sm:text-sm uppercase tracking-[0.1em] sm:tracking-[0.15em] font-bold mb-4">
                    visionOS App Ratings
                  </p>
                  <div className="glass no-hover grid grid-cols-4 gap-1 sm:gap-4 px-2 sm:px-6 py-6 sm:py-8 rounded-2xl">
                    {project.ratingsComparison.map((item, i) => (
                      <div key={i} className="text-center">
                        <div className={`text-[clamp(1.25rem,3vw,2rem)] font-bold mb-1 sm:mb-2 ${i === 0 ? 'gradient-text' : 'text-[rgba(var(--foreground-rgb),0.4)]'}`}>
                          {item.value}
                        </div>
                        <div className={`text-[0.6rem] sm:text-sm uppercase tracking-[0.02em] sm:tracking-[0.15em] font-bold ${i === 0 ? 'gradient-text' : 'text-[rgba(var(--foreground-rgb),0.5)]'}`}>
                          {item.label}
                        </div>
                      </div>
                    ))}
                  </div>
                  {project.ratingsComparisonCaption && (
                    <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-3 text-center">
                      {project.ratingsComparisonCaption}
                    </p>
                  )}
                </div>
              )}
            </section>
          )}

          {/* User Quotes */}
          {project.userQuotes && project.userQuotes.length > 0 && (
            <section className="mb-12">
              <SectionHeader crtEnabled={crtEnabled} size="small">{project.userQuotesHeader ?? "From Fans"}</SectionHeader>
              <div className="space-y-4">
                {project.userQuotes.map((quote, i) => (
                  <QuoteCard key={i} quote={quote} />
                ))}
              </div>
            </section>
          )}

          {/* Team Quotes */}
          {project.teamQuotes && project.teamQuotes.length > 0 && (
            <section className="mb-12">
              <SectionHeader crtEnabled={crtEnabled} size="small">From the Team</SectionHeader>
              <div className="space-y-4">
                {project.teamQuotes.map((quote, i) => (
                  <QuoteCard key={i} quote={quote} />
                ))}
              </div>
            </section>
          )}

          {/* Leadership Quotes */}
          {project.leadershipQuotes && project.leadershipQuotes.length > 0 && (
            <section className="mb-12">
              <SectionHeader crtEnabled={crtEnabled} size="small">{project.leadershipQuotes[0]?.context || "From Leadership"}</SectionHeader>
              <div className="space-y-4">
                {project.leadershipQuotes.map((quote, i) => (
                  <div key={i} className="glass no-hover relative p-6 rounded-2xl">
                    {quote.publication && (
                      <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mb-2">{quote.publication}</p>
                    )}
                    <p className="text-sm text-[var(--cs-body-text)] leading-relaxed">
                      “{parseQuoteText(quote.text, `leadership-quote-${i}-`)}”
                    </p>
                    <p className="text-xs text-[rgba(var(--foreground-rgb),0.5)] mt-2">
                      — {quote.author}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Press Credentials */}
          {project.pressCredentials && project.pressCredentials.length > 0 && (
            <section className="mb-12">
              <SectionHeader crtEnabled={crtEnabled} size="small">From Press</SectionHeader>
              <div className="space-y-4">
                {project.pressCredentials.map((credential, i) => (
                  <div key={i} className="glass no-hover relative p-6 rounded-2xl">
                    {credential.year && (
                      <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mb-2">{credential.year}</p>
                    )}
                    <p className="text-sm text-[var(--cs-body-text)] leading-relaxed">
                      {parseContent(credential.text, `press-${i}-`)}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Divider before Takeaways */}
          {project.learnings && project.learnings.length > 0 && (
            <hr className="border-t border-[rgba(var(--foreground-rgb),0.1)] mb-12" />
          )}

          {/* Learnings / Takeaways */}
          {project.learnings && project.learnings.length > 0 && (
            <section id="takeaways" className="mb-12">
              <SectionHeader crtEnabled={crtEnabled}>Takeaways</SectionHeader>
              <ul className="space-y-3">
                {project.learnings.map((learning, i) => (
                  <li key={`learning-${i}`} className="flex gap-3 text-base text-[var(--cs-body-text)]">
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: 'rgba(var(--theme-base-rgb), 0.15)',
                        color: 'var(--theme-base)',
                      }}
                    >
                      <ArrowRightIcon size={14} />
                    </span>
                    <span>{parseContent(learning, `learning-${i}-`)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Recognition Images (Challenge Coin, etc.) */}
          {project.recognitionImages && project.recognitionImages.length > 0 && (
            <div className={`grid ${project.recognitionImages.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-4 mb-12`}>
              {project.recognitionImages.map((img, i) => (
                <div key={i}>
                  <MediaWithFallback
                    src={img.src}
                    alt={img.caption || "Recognition"}
                    className="w-full rounded-2xl cursor-zoom-in"
                    onClick={() => openLightbox(img.src)}
                    onMouseEnter={handleProjectHover}
                    tabIndex={0}
                    onKeyDown={handleKeyboardClick(() => openLightbox(img.src))}
                  />
                  {img.caption && (
                    <p className="text-xs text-[rgba(var(--foreground-rgb),0.4)] mt-2 text-center">
                      {img.caption}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Divider before Conclusion */}
          {project.conclusion && (
            <hr className="border-t border-[rgba(var(--foreground-rgb),0.1)] mb-12" />
          )}

          {/* Conclusion */}
          {project.conclusion && (
            <section className="mb-12 text-center">
              <div className="text-base text-[var(--cs-body-text)] leading-relaxed">
                {renderParagraphs(project.conclusion, 'conclusion-')}
              </div>
              <p className="mt-6 text-lg font-semibold gradient-text">(｡•̀ᴗ-)✧</p>
            </section>
          )}

          {/* Next/Previous Project Navigation */}
          <section className="border-t border-[rgba(var(--foreground-rgb),0.1)] pt-8 space-y-6">
            {nextProject && (
              <div>
                <p className="text-xs uppercase tracking-widest font-black text-[rgba(var(--foreground-rgb),0.5)] mb-3">Next Project</p>
                <ProjectCard
                  project={nextProject}
                  index={0}
                  onClick={() => onNavigateToProject?.(nextProject.id)}
                  isUnlocked={true}
                  hideIndex
                  compact
                  noAnimation
                  noMagnetic
                  trackingSource="case_study_nav"
                />
              </div>
            )}
            {prevProject && (
              <div>
                <p className="text-xs uppercase tracking-widest font-black text-[rgba(var(--foreground-rgb),0.5)] mb-3">Previous Project</p>
                <ProjectCard
                  project={prevProject}
                  index={0}
                  onClick={() => onNavigateToProject?.(prevProject.id)}
                  isUnlocked={true}
                  hideIndex
                  compact
                  arrowLeft
                  noAnimation
                  noMagnetic
                  trackingSource="case_study_nav"
                />
              </div>
            )}
          </section>
    </div>

    {/* Floating section nav - right edge, appears on scrollbar hover (desktop) or tap (mobile) */}
    <div
      className="fixed right-2 top-0 bottom-0 w-16 z-[115] flex items-center justify-end pointer-events-none"
    >
      {/* Hover trigger strip */}
      <div
        className="absolute right-0 top-0 bottom-0 w-16 pointer-events-auto"
        onMouseEnter={() => !isMobile && setNavHovered(true)}
        onMouseLeave={() => !isMobile && setNavHovered(false)}
        onClick={() => isMobile && setMobileNavOpen(prev => !prev)}
      />
      <nav
        aria-label="Case study sections"
        className="mr-8"
        style={{
          transform: `translateX(${(isMobile ? mobileNavOpen : navHovered) ? 0 : 20}px)`,
          opacity: (isMobile ? mobileNavOpen : navHovered) ? 1 : 0,
          transition: 'opacity var(--duration-normal) ease, transform var(--duration-normal) ease',
          pointerEvents: (isMobile ? mobileNavOpen : navHovered) ? 'auto' : 'none',
        }}
        onMouseEnter={() => !isMobile && setNavHovered(true)}
        onMouseLeave={() => !isMobile && setNavHovered(false)}
        onTouchStart={() => {
          if (isMobile && mobileNavOpen && mobileNavTimeoutRef.current) {
            clearTimeout(mobileNavTimeoutRef.current);
            mobileNavTimeoutRef.current = setTimeout(() => setMobileNavOpen(false), 2500);
          }
        }}
      >
        <div
          className="glass flex flex-col gap-0.5 px-1.5 py-1.5 rounded-2xl overflow-hidden"
          style={{
            isolation: 'isolate',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
          }}
        >
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                setActiveSection(section.id);
                scrollToSection(section.id);
                if (isMobile) setMobileNavOpen(false);
              }}
              className="relative px-4 py-2 text-[0.8rem] font-medium rounded-2xl whitespace-nowrap text-center cursor-pointer"
              style={{
                color: activeSection === section.id
                  ? 'black'
                  : 'rgba(var(--foreground-rgb), 0.7)',
                background: activeSection === section.id
                  ? 'var(--theme-base)'
                  : 'transparent',
                transition: 'color var(--duration-fast) ease, background var(--duration-fast) ease, transform var(--duration-fast) ease',
              }}
              onMouseEnter={(e) => {
                handleProjectHover();
                if (activeSection !== section.id) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.95)';
                  e.currentTarget.style.transform = 'scale(1.03)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== section.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(var(--foreground-rgb), 0.7)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {section.label}
            </button>
          ))}
        </div>
      </nav>
    </div>

    {/* Lightbox */}
    {lightboxIndex !== null && (
      <LightboxV2
        items={lightboxItems}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    )}
    </>
    </ProjectNavContext.Provider>
  );
}
